"use client";

import { EntityViewComponentMapping } from "@/services/entity-view-components.service";
import { OptionType, SelectedOptionsType } from "./relation-instance-select";
import { useInView } from "@/hooks/ui-helpers/use-in-view";
import {
  PendingChange,
  ViewComponentRenderer,
} from "@/components/relation-display-components/view-component-renderer";
import { Ref, useEffect } from "react";

interface SelectedItemListProps {
  selectedOptions: SelectedOptionsType;
  options: Array<{ id: string; [fieldName: string]: string }>;
  onDelete?: (id: string) => void;
  onOptionClick?: (id: string) => void;
  selectedSubId?: string;
  pendingChangeOf?: (id: string) => PendingChange | undefined;
  idsToDisconnect: Set<string>;
  entityName: string;
  relationFieldToDisplay?: string;
  mapping?: EntityViewComponentMapping;
}

export const SelectedItemList: React.FC<SelectedItemListProps> = ({
  selectedOptions,
  idsToDisconnect,
  options,
  selectedSubId,
  onDelete,
  onOptionClick,
  pendingChangeOf,
  entityName,
  relationFieldToDisplay,
  mapping,
}) => {
  const pages = selectedOptions?.data?.pages.filter((x) => !!x) ?? [];
  const selectedOptionElements = pages.flatMap((page) =>
    Array.isArray(page.data) ? page.data.map((o) => o as OptionType) : [],
  );

  // Build a map keyed by id: start with query results, then override with options prop
  const displayMap = new Map(selectedOptionElements.map((x) => [x.id, x]));

  // Override with items from options prop (these come from allOptions with bufferedOptions)
  // but merge to avoid downgrading a fully-fetched item to a minimal { id } placeholder
  options.forEach((opt) => {
    const existing = displayMap.get(opt.id) ?? {};
    displayMap.set(opt.id, { ...existing, ...opt });
  });

  const [ref, inView] = useInView();

  useEffect(() => {
    if (inView && selectedOptions.hasNextPage && !selectedOptions.isFetching) {
      selectedOptions.fetchNextPage();
    }
  }, [inView, selectedOptions]);

  return (
    <div className="mt-1 divide-y rounded-sm bg-background text-sm">
      <div className="max-h-48 space-y-1 overflow-y-auto">
        {Array.from(displayMap.values()).map((option, idx) => {
          if (idsToDisconnect.has(option.id)) {
            return null;
          }
          return (
            <ViewComponentRenderer
              key={idx} // need to use idx because id is null for newly created local items
              option={option}
              id={option.id}
              onDelete={onDelete}
              onOptionClick={onOptionClick}
              isHighlighted={selectedSubId === option.id}
              pendingChange={pendingChangeOf?.(option.id)}
              entityName={entityName}
              integer_id={option.integer_id}
              relationFieldToDisplay={relationFieldToDisplay}
              mapping={mapping}
            />
          );
        })}
        <div ref={ref as Ref<HTMLDivElement>}></div>
      </div>
    </div>
  );
};
